import React, {
  useState,
  useEffect,
  useRef,
  ReactComponentElement,
} from "react";
import {
  Button,
  Pagination,
  Typography,
  Grid,
  SvgIcon,
  Box,
  Card,
  LinearProgress,
  Paper,
  useMediaQuery,
  useTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  Snackbar,
  Alert,
  AlertColor,
  Slider,
} from "@mui/material";
import "./OnlineExampageTamplete.scss";
import { ReactComponent as Deleteicon } from "../../../Assets/Images/delete.svg";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import InfoIcon from "@mui/icons-material/Info";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { ReactComponent as Logo } from "../../../Assets/Images/loginlogo.svg";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LucideImage,
  LucideZoomIn,
  LucideZoomOut,
  Pickaxe,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../../Redux/Store/Hooks';
import {
  EditQuestions,
  clearEditQuestionsResponse,
} from '../../../Redux/Slices/Questions/EditQuestionsSlice';

interface Question {
  id: number;
  question_type: string;
  prompt: string;
  instruction: string;
  subtype_name: string;
  question_id: number;
  question: string;
  question_url?: string;
  prompt_url?: string;
  prompt_imgWidth?: number;
  imgWidth?: number;
  question_type_id?: number;
  options: { optionname: string; option_id: number }[];
  optionsurl: { optionname: string; option_id: number }[];
}

// Register necessary modules
const FontSize = ReactQuill.Quill.import("formats/size");
FontSize.whitelist = ["small", false, "large", "huge"];
ReactQuill.Quill.register(FontSize, true);

const modules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote"],
  ],
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

const formats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "script",
  "list",
  "bullet",
  "indent",
  "direction",
  "blockquote",
  "align",
];

interface OnlineExamTampleteProps {
  previewData?: any;
  onBack?: () => void;
}

const OnlineExamTamplete: React.FC<OnlineExamTampleteProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const examtamplete = props.previewData || location.state;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  let adaptImageWidth = useMediaQuery("(min-width: 768px)")


  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: number]: string | null;
  }>({});
  const [selectedAnswersOption, setSelectedAnswersOption] = useState<
    Record<number, Record<number, number>>
  >({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<number>>(
    new Set()
  );
  const [showFlagAlert, setShowFlagAlert] = useState(false);
  const [flagAlertMessage, setFlagAlertMessage] = useState("");

  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor | undefined;
  }>({
    open: false,
    message: "",
    severity: undefined,
  });
  // Text Editor State
  const [editorValue, setEditorValue] = useState<string>("");
  const [wordCount, setWordCount] = useState(0);
  const [remainingWords, setRemainingWords] = useState(0);
  const [characterCount, setCharacterCount] = useState(0);
  const [sentenceCount, setSentenceCount] = useState(0);
  const [paragraphCount, setParagraphCount] = useState(0);

  // Mock questions data based on exam template
  const [questions, setQuestions] = useState<Question[]>([
    {
      id: 1,
      question_type: examtamplete?.Question_type_name || "General",
      prompt: examtamplete?.prompt || "",
      instruction: examtamplete?.instruction || "",
      subtype_name: examtamplete?.Question_type_name || "General",
      question_id: 1,
      question: examtamplete?.question || "",
      question_url: examtamplete?.question_url || "",
      imgWidth: examtamplete?.imgWidth || 100,
      prompt_url: examtamplete?.prompt_url || "",
      prompt_imgWidth: examtamplete?.prompt_imgWidth || 100,
      options:
        examtamplete?.options?.map((opt: any) => ({
          optionname: opt?.option,
          option_id: opt?.id,
        })) || [],
      optionsurl:
        examtamplete?.options?.map((opt: any) => ({
          optionname: opt?.option_url === null ? null : opt?.option_url,
          option_id: opt?.id,
        })) || [],
    },
  ]);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentQuestion]);





  const handleAnswerSelect = (questionId: number, option: string) => {
    setSelectedAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: option,
    }));
  };

  const clearSelection = () => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestion]: null,
    }));

    setSelectedAnswersOption((prev) => {
      const newState = { ...prev };
      if (newState[0]) {
        newState[0] = { ...newState[0] };
        delete newState[0][currentQuestion];
        if (Object.keys(newState[0]).length === 0) {
          delete newState[0];
        }
      }
      return newState;
    });
  };

  const toggleFlag = () => {
    const isCurrentlyFlagged = flaggedQuestions.has(currentQuestion);
    setFlaggedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(currentQuestion)) {
        newSet.delete(currentQuestion);
        setFlagAlertMessage("Question unflagged");
      } else {
        newSet.add(currentQuestion);
        setFlagAlertMessage("Question flagged for review");
      }
      return newSet;
    });
    setShowFlagAlert(true);
  };

  const handleChange = (content: any) => {
    const textContent = content?.replace(/<[^>]+>/g, "")?.trim();
    const words = textContent?.split(/\s+/);

    setEditorValue(content);
    setWordCount(words?.length || 0);

    const remainingWords = 120 - (words?.length || 0);
    setRemainingWords(remainingWords);

    const characterCount = textContent?.replace(/\s+/g, "")?.length || 0;
    setCharacterCount(characterCount);

    const sentences =
      textContent
        ?.split(/[.!?]+/)
        .filter((sentence: any) => sentence?.trim()?.length > 0) || [];
    setSentenceCount(sentences.length);

    const paragraphs =
      textContent
        ?.split(/\n+/)
        .filter((para: any) => para?.trim()?.length > 0) || [];
    setParagraphCount(paragraphs?.length || 0);
  };

  const progress = (currentQuestion / questions?.length) * 100;
  const height = isMobile ? "40vh" : isTablet ? "70vh" : "70vh";
  const isFlagged = flaggedQuestions.has(currentQuestion);
  const isOptionSelected = !!selectedAnswersOption[0]?.[currentQuestion];
  const imageFile = questions[currentQuestion - 1]?.question_url;
  const promptImageFile = questions[currentQuestion - 1]?.prompt_url;

  const [imageWidth, setImageWidth] = useState<number>(0);
  const [promptImageWidth, setPromptImageWidth] = useState<number>(0);
  const [savingType, setSavingType] = useState<'prompt' | 'question' | null>(null);

  useEffect(() => {
    setImageWidth(questions[currentQuestion - 1]?.imgWidth ?? 90);
    setPromptImageWidth(questions[currentQuestion - 1]?.prompt_imgWidth ?? 90);
  }, [currentQuestion, questions]);



  const dispatch = useAppDispatch();

  const { data: editQuestionResponse, isLoading: isEditLoading } =
    useAppSelector(
      (state) => state.EditQuestionsSlice
    );

  const handleBack = () => {
    if (props.onBack) {
      props.onBack();
    } else {
      navigate("/masters/questions");
      dispatch(clearEditQuestionsResponse());
    }
  };

  const presets = [
    { label: "Small (70%)", value: 30 },
    { label: "Medium (100%)", value: 60 },
    { label: "Large (130%)", value: 90 },
  ];


  useEffect(() => {
    if (editQuestionResponse && Object.keys(editQuestionResponse).length > 0) {
      if (editQuestionResponse.status) {
        setAlert({
          open: true,
          message:
            "Image size updated",
          severity: "success",
        });
      } else {
        setAlert({
          open: true,
          message: "Image size update failed",
          severity: "error",
        });
      }
      setSavingType(null);
      dispatch(clearEditQuestionsResponse());
    }
  }, [editQuestionResponse, dispatch]);


  const renderQuestionOptions = (
    questionId: number,
    question_type: string,
    options: { optionname: string; option_id: number }[],
    optionsurl: { optionname: string; option_id: number }[]
  ) => {
    const hasNullUrl = optionsurl.some((option) => option.optionname === null);

    return (
      <Grid
        container
        spacing={3}
        className="ident3x"
        sx={{
          ...(question_type === "Reading Comprehension" ||
            question_type === "Writing"
            ? {
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }
            : {
              margin: "0px",
              width: "100%"
            }),
        }}
      >
        {question_type === "Writing" ? (
          <>
            <ReactQuill
              className="custom-quill"
              style={{
                width: "100%",
                padding: "20px 0px 20px 0px",
                height: height,
                borderRadius: "20px",
                marginLeft: "25px",
              }}
              theme="snow"
              value={editorValue}
              onChange={handleChange}
              modules={modules}
              formats={formats}
              tabIndex={0}
              placeholder="Write something here..."
              preserveWhitespace
              data-gramm="false"
              onFocus={() => {
                const editorElement = document.querySelector(".ql-editor");
                if (editorElement) {
                  editorElement.setAttribute("spellcheck", "false");
                  editorElement.setAttribute("autocorrect", "off");
                  editorElement.setAttribute("autocapitalize", "off");
                  editorElement.setAttribute("data-gramm", "false");
                  (editorElement as HTMLElement).style.fontSize = "14px";
                }
              }}
            />
          </>
        ) : hasNullUrl ? (
          options.map((option, index) => {
            const isSelected =
              selectedAnswersOption[0]?.[currentQuestion] === option.option_id;

            return (
              <Grid item xs={12} key={index} className="optionbox-wrapper">
                <Box
                  onClick={() => {
                    setSelectedAnswersOption((prev) => ({
                      ...prev,
                      [0]: {
                        ...(prev[0] || {}),
                        [currentQuestion]: option.option_id,
                      },
                    }));
                    handleAnswerSelect(questionId, option.optionname);
                  }}
                  className="ident"
                  sx={{
                    border: isSelected
                      ? "1.2px solid #009dff"
                      : "1px solid #CDCDCD",
                    borderRadius: "10px",
                    padding:
                      question_type === "Reading Comprehension"
                        ? "10px"
                        : "8px",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "15px",
                    minHeight: "50px",
                    backgroundColor: isSelected ? "#EFF6FF" : "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isSelected ? "#EFF6FF" : "#f5f5f5",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      minWidth: 28,
                      borderRadius: "50%",
                      backgroundColor: isSelected ? "#009dff" : "#F0F1F7",
                      color: isSelected ? "#fff" : "#000",
                      fontWeight: "500",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    {String.fromCharCode(65 + index)}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      color: isSelected ? "#333" : "#000",
                      fontWeight: 500,
                      fontSize: "16px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {option.optionname}
                  </Box>
                </Box>
              </Grid>
            );
          })
        ) : (
          optionsurl.map((option, index) => {
            const isSelected =
              selectedAnswersOption[0]?.[currentQuestion] === option.option_id;

            return (
              <Grid item xs={6} key={index} className="gridwidth">
                <Box
                  onClick={() => {
                    setSelectedAnswersOption((prev) => ({
                      ...prev,
                      [0]: {
                        ...(prev[0] || {}),
                        [currentQuestion]: option.option_id,
                      },
                    }));
                    handleAnswerSelect(questionId, option.optionname);
                  }}
                  sx={{
                    border: isSelected
                      ? "1.2px solid #009dff"
                      : "1px solid #CDCDCD",
                    borderRadius: "10px",
                    padding:
                      question_type === "Reading Comprehension"
                        ? "10px"
                        : "8px",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "15px",
                    backgroundColor: isSelected ? "#EFF6FF" : "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isSelected ? "#EFF6FF" : "#f5f5f5",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      minWidth: 28,
                      borderRadius: "50%",
                      backgroundColor: isSelected ? "#009dff" : "#F0F1F7",
                      color: isSelected ? "#fff" : "#000",
                      fontWeight: "500",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    {String.fromCharCode(65 + index)}
                  </Box>
                  <Box
                    component="img"
                    src={option.optionname}
                    alt={`Option ${index + 1}`}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "60px",
                      objectFit: "contain",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
              </Grid>
            );
          })
        )}
      </Grid>
    );
  };

  return (
    <>
      <Box sx={{ display: "flex", height: "1px" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "#FFFFFF",
            display: "flex",
            boxShadow: "0px 4px 40px 0px #0000000D",
          }}
        >
          <Toolbar
            sx={{
              minHeight: "0px",
              display: "flex",
              justifyContent: "space-between",
              paddingLeft: "0px",
              paddingRight: "0px",
              margin:
                questions[0]?.question_type === "Writing" ? "0% 0.5%" : "0% 3%",
              height: "50px",
            }}
          >
            <Typography variant="h6" noWrap component="div">
              <SvgIcon
                sx={{
                  width: "123px",
                  height: "50px",
                  marginTop: "10px",
                  cursor: "pointer",
                }}
                color="primary"
                component={Logo}
                inheritViewBox
                onClick={handleBack}
              />
            </Typography>
            <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
              <div
                style={{
                  display: "flex",
                  color: "#009dff",
                  fontSize: "16px",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={handleBack}
              >
                <ArrowBackIosIcon sx={{ color: "#009dff" }} />
                <div style={{ color: "#009dff", fontSize: "16px" }}>
                  Back to Tests
                </div>
              </div>
              <div className="onlineexamtime">
                <AccessTimeIcon sx={{ marginRight: "5px" }} /> 00:00
              </div>
            </div>
          </Toolbar>
        </AppBar>
      </Box>

      <div style={{ height: "50px" }} />

      {/* Main content container with flex layout for sticky nav */}
      <Box
        ref={scrollRef}
        className="unselectable"
        sx={{
          backgroundColor: "#f5f9ff",
          height: "calc(100vh - 50px)", // Full viewport minus appbar
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Prevent outer scroll; handle inner scrolls
        }}
      >
        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto", // Enable vertical scroll for content
            pb: 8, // Padding bottom to account for sticky nav height (adjust if nav height changes)
          }}
        >
          <div
            style={{
              display: "flex",
              color: "#1e3a8a",
              fontWeight: 700,
              fontSize: "24px",
              flexWrap: "wrap",
              columnGap: "13px",
              padding: "5px 0px",
              margin:
                questions[0]?.question_type === "Writing"
                  ? "2% 1% 0.5%"
                  : "2% 4.5% 0.5% 4.5%",
              alignItems: "center",
            }}
          >
            {examtamplete?.test_name || "Practice Test"}&nbsp;
            <span
              style={{
                color: "#009dff",
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                marginTop: "6px",
                fontWeight: "500",
              }}
            >
              • Question {currentQuestion} of {questions?.length}
            </span>
          </div>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              margin:
                questions[0]?.question_type === "Writing"
                  ? "0% 1%"
                  : "0% 4.5% 1%",
              padding: "5px",
              borderRadius: "10px",
            }}
          />

          {questions[0]?.question_type !== "Writing" && (
            <div style={{ margin: "0.5% 4.5%" }}>
              <Pagination
                count={questions.length}
                variant="outlined"
                siblingCount={6}
                boundaryCount={1}
                page={currentQuestion}
                onChange={(event, value) => setCurrentQuestion(value)}
                renderItem={(item: any) => {
                  if (item.type === "previous" || item.type === "next")
                    return null;

                  const currentSectionAnswers = selectedAnswersOption[0] || {};
                  const isAnswered = Boolean(currentSectionAnswers[item.page]);
                  const isFlagged = flaggedQuestions.has(item.page);
                  const backgroundColor =
                    item.page === currentQuestion
                      ? "linear-gradient(to right, #00aaff, #88e1ff)"
                      : isFlagged
                        ? "#ffffff"
                        : isAnswered
                          ? "#bfdbfe"
                          : "#9CA3AF";

                  return (
                    <Box
                      key={item.page}
                      position="relative"
                      display="inline-block"
                    >
                      <Button
                        sx={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "50%",
                          color:
                            isFlagged && item.page !== currentQuestion
                              ? "#f97316"
                              : isAnswered
                                ? "#009dff"
                                : isFlagged && item.page === currentQuestion
                                  ? "#ffff"
                                  : "white",
                          background: backgroundColor,
                          minWidth: "unset",
                          padding: 0,
                          marginLeft: "5px",
                          marginTop: "3px",
                          fontSize: "17px",
                          transition: "background 0.2s, opacity 0.2s",
                          border:
                            isFlagged && isAnswered
                              ? "2px solid #f97316"
                              : isFlagged && item.page !== currentQuestion
                                ? "2px solid #f97316"
                                : isFlagged && item.page === currentQuestion
                                  ? "none"
                                  : "none",
                          "&:hover": {
                            backgroundColor:
                              item.page === currentQuestion
                                ? "#4dc4ff"
                                : "#d1d5db",
                            opacity: item.page === currentQuestion ? 1 : 0.8,
                          },
                        }}
                        onClick={() => setCurrentQuestion(item.page)}
                      >
                        {item.page}
                      </Button>
                      {isFlagged && (
                        <FlagOutlinedIcon
                          sx={{
                            backgroundColor: "#f97316",
                            color: "white",
                            position: "absolute",
                            top: "-8px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: "18px",
                            borderRadius: "50%",
                          }}
                        />
                      )}
                    </Box>
                  );
                }}
              />
            </div>
          )}
          {questions[currentQuestion - 1]?.question_type ===
            "Reading Comprehension" ||
            (questions[currentQuestion - 1]?.question_type ===
              "Quantitative Reasoning" &&
              (questions[currentQuestion - 1]?.prompt_url !== null && questions[currentQuestion - 1]?.prompt_url !== undefined)) ? (


            <>
              <Card
                sx={{
                  m: 2,
                  borderRadius: "20px",
                  // height: "100%",
                  margin: "0px 4% 10px 4%",
                  overflow: "visible",
                  boxShadow: "0px 37px 37px rgba(0, 157, 255, 0.1)",
                  marginBottom: "75px",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "white",
                    height: "35px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "16px",
                    fontWeight: 500,
                    textTransform: "capitalize",
                    padding: "25px",
                    backgroundColor: "#009dff",
                    mb: 1,
                    borderRadius: "10px 10px 0px 0px",
                  }}
                >
                  {questions[currentQuestion - 1]?.subtype_name}
                </Typography>
                <div className="overonlinedi">
                  <div
                    style={{
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        color: "#111827",
                        fontWeight: 600,
                        fontSize: "1.7rem",
                        marginBottom: "20px",
                      }}
                    >
                      Passage:
                    </div>
                    <Typography
                      className="readingpassage"
                      sx={{
                        height: "600px",
                        overflow: "scroll",
                        fontWeight: 400,
                        border: "1px solid #cecece",
                        padding: "20px",
                        borderRadius: "10px",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          marginBottom: "20px",
                          fontSize: "17px",
                          fontWeight: "500",
                          whiteSpace: "break-spaces",
                        }}
                      >
                        {questions[currentQuestion - 1]?.prompt}
                      </Typography>
                      {promptImageFile && (
                        <div className="w-full rounded-xl border border-yellow-300 bg-yellow-50 p-5 shadow-sm">
                          <h2
                            className="font-bold flex items-center gap-2"
                            style={{ color: "#c96726b9", fontSize: "1.5rem" }}
                          >
                            <span>
                              <LucideImage />
                            </span>{" "}
                            Image Size Control
                          </h2>

                          <div
                            className="mt-2"
                            style={{ color: "#c96726b9", fontSize: "1.2rem" }}
                          >
                            Adjust the image size to ensure optimal display.
                          </div>

                          {/* Presets */}
                          <div className="mt-3 flex flex-wrap gap-2 items-center">
                            <span className="text-lg text-black-900">Presets:</span>
                            {presets.map((preset) => (
                              <button
                                key={preset.value}
                                onClick={() => setPromptImageWidth(preset.value)}
                                className={`px-3 py-1.5 rounded-lg border transition text-sm
              ${promptImageWidth == preset.value
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                  }`}
                              >
                                <span className={`text-lg font-bold`}>
                                  {preset.label}
                                </span>
                              </button>
                            ))}
                          </div>

                          {/* Slider */}
                          <div className="mt-2 flex items-center gap-3 w-full">
                            <span className="text-gray-500">
                              <LucideZoomOut size={18} />
                            </span>

                            <Slider
                              value={promptImageWidth}
                              min={20}
                              max={100}
                              step={1}
                              onChange={(_, value) => setPromptImageWidth(value as number)}
                            />

                            <span className="text-gray-500">
                              <LucideZoomIn size={18} />
                            </span>

                            <span className="font-bold text-lg">{promptImageWidth + 40}%</span>
                          </div>

                          {/* Save button */}
                          <div className="mt-2">
                            <button
                              className="bg-orange-500 text-white text-lg px-5 py-2 rounded-lg shadow hover:bg-orange-600 transition"
                              onClick={async () => {
                                setSavingType('prompt');
                                dispatch(
                                  EditQuestions({
                                    id: examtamplete?.id,
                                    promptImgWidth: promptImageWidth,
                                  })
                                );
                              }}
                            >
                              {isEditLoading && savingType === 'prompt'
                                ? 'Saving...'
                                : 'Save Image Size to Database'}
                            </button>
                          </div>
                        </div>
                      )}
                      <br />
                      {questions[0]?.question_url && (
                        <Snackbar
                          open={alert.open}
                          autoHideDuration={6000}
                          onClose={() => setAlert({ ...alert, open: false })}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          sx={{
                            bottom: { xs: '90px', sm: '80px' },
                          }}
                        >
                          <Alert
                            onClose={() => setAlert({ ...alert, open: false })}
                            severity={alert.severity}
                            sx={{ width: '100%', backgroundColor: "#fff", fontWeight: "600" }}
                          >
                            {alert.message}
                          </Alert>
                        </Snackbar>
                      )}
                      <br />
                      {questions[currentQuestion - 1]?.prompt_url && (
                        <Box sx={{ mb: 2, textAlign: "center", width: "100%", alignItems: "center", justifyContent: "center", display: "flex" }}>
                          <img
                            src={questions[currentQuestion - 1]?.prompt_url}
                            alt="Prompt View"
                            className="resizable-image"
                            style={{ width: `${promptImageWidth}%` }}
                          />
                        </Box>
                      )}
                      <Typography
                        variant="h5"
                        sx={{
                          marginBottom: "20px",
                          fontSize: "16px",
                          fontWeight: "500",
                          whiteSpace: "break-spaces",
                          lineHeight: "1.625",
                        }}
                      >
                        {questions[currentQuestion - 1]?.instruction}
                      </Typography>
                    </Typography>
                  </div>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      width: "100%",
                      padding: "10px",
                    }}
                  >
                    <div className="examscrollreading" style={{ width: "100%" }}>
                      <Typography variant="h5">
                        <div
                          style={{
                            color: "#111827",
                            fontWeight: 600,
                            fontSize: "1.7rem",
                            marginBottom: "20px",
                          }}
                        >
                          Question:
                        </div>
                        <Typography
                          sx={{
                            fontSize: "1.4rem",
                            marginBottom: "20px",
                            textAlign: "left",
                            fontWeight: 500,
                          }}
                        >
                          {questions[currentQuestion - 1]?.question}
                        </Typography>

                        {questions[currentQuestion - 1]?.question_url && (
                          <Typography
                            variant="h5"
                            sx={{
                              marginBottom: "20px",
                              textAlign: "left",
                              position: "relative", // Ensure proper positioning of the image
                              overflow: "visible", // Ensure the enlarged image is not clipped
                              "& img": {
                                maxWidth: "100%",
                                height:
                                  questions[0]
                                    ?.question_type === "Writing"
                                    ? "30rem"
                                    : "100%",
                                cursor: "pointer",
                                transition: "transform 0.3s ease",
                                position: "relative",
                                transformOrigin: "left center", // Keep the hover scaling aligned to the left
                              },
                              // "& img:hover": {
                              //   transform: "scale(1.5)",
                              // },
                            }}
                          >
                            {imageFile && (
                              <div className="w-full rounded-xl border border-yellow-300 bg-yellow-50 p-5 shadow-sm">
                                <h2
                                  className="font-bold flex items-center gap-2"
                                  style={{ color: "#c96726b9", fontSize: "1.5rem" }}
                                >
                                  <span>
                                    <LucideImage />
                                  </span>{" "}
                                  Image Size Control
                                </h2>

                                <div
                                  className="mt-2"
                                  style={{ color: "#c96726b9", fontSize: "1.2rem" }}
                                >
                                  Adjust the image size to ensure optimal display.
                                </div>

                                {/* Presets */}
                                <div className="mt-3 flex flex-wrap gap-2 items-center">
                                  <span className="text-lg text-black-900">Presets:</span>
                                  {presets.map((preset) => (
                                    <button
                                      key={preset.value}
                                      onClick={() => setImageWidth(preset.value)}
                                      className={`px-3 py-1.5 rounded-lg border transition text-sm
              ${imageWidth == preset.value
                                          ? "bg-blue-600 text-white border-blue-600"
                                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                        }`}
                                    >
                                      <span className={`text-lg font-bold`}>
                                        {preset.label}
                                      </span>
                                    </button>
                                  ))}
                                </div>

                                {/* Slider */}
                                <div className="mt-2 flex items-center gap-3 w-full">
                                  <span className="text-gray-500">
                                    <LucideZoomOut size={18} />
                                  </span>

                                  <Slider
                                    value={imageWidth}
                                    min={20}
                                    max={100}
                                    step={1}
                                    onChange={(_, value) => setImageWidth(value as number)}
                                  />

                                  <span className="text-gray-500">
                                    <LucideZoomIn size={18} />
                                  </span>

                                  <span className="font-bold text-lg">{imageWidth + 40}%</span>
                                </div>

                                {/* Save button */}
                                <div className="mt-2">
                                  <button
                                    className="bg-orange-500 text-white text-lg px-5 py-2 rounded-lg shadow hover:bg-orange-600 transition"
                                    onClick={async () => {
                                      setSavingType('question');
                                      dispatch(
                                        EditQuestions({
                                          id: examtamplete?.id,
                                          imgWidth: imageWidth,
                                        })
                                      );
                                    }}
                                  >
                                    {isEditLoading && savingType === 'question'
                                      ? 'Saving...'
                                      : 'Save Image Size to Database'}
                                  </button>
                                </div>
                              </div>
                            )}
                            <br />
                            {questions[0]?.question_url && (
                              <Snackbar
                                open={alert.open}
                                autoHideDuration={6000}
                                onClose={() => setAlert({ ...alert, open: false })}
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'right',
                                }}
                                sx={{
                                  bottom: { xs: '90px', sm: '80px' },
                                }}
                              >
                                <Alert
                                  onClose={() => setAlert({ ...alert, open: false })}
                                  severity={alert.severity}
                                  sx={{ width: '100%', backgroundColor: "#fff", fontWeight: "600" }}
                                >
                                  {alert.message}
                                </Alert>
                              </Snackbar>
                            )}
                            <br />
                            <Box sx={{ mb: 2, textAlign: "center", width: "100%", alignItems: "center", justifyContent: "center", display: "flex" }}>
                              <img src={imageFile} alt="Uploaded preview"
                                className="resizable-image"
                                style={{ width: `${imageWidth}%` }} />
                            </Box>
                          </Typography>
                        )}

                        <div
                          style={{
                            color: "#111827",
                            fontWeight: 600,
                            fontSize: "1.7rem",
                            marginBottom: "20px",
                          }}
                        >
                          Select your answer:
                        </div>
                        <Typography
                          variant="h5"
                          sx={{
                            marginBottom: "20px",
                            textAlign: "justify",
                            whiteSpace: "break-spaces",
                          }}
                        >
                          {questions?.length === 0 ? (
                            <div
                              style={{
                                textAlign: "center",
                                fontSize: "larger",
                                marginTop: "30%",
                              }}
                            >
                              No Question Found
                            </div>
                          ) : (
                            renderQuestionOptions(
                              currentQuestion,
                              questions[currentQuestion - 1]?.question_type,
                              questions[currentQuestion - 1]?.options || [],
                              questions[currentQuestion - 1]?.optionsurl || []
                            )
                          )}{" "}
                        </Typography>
                        <Box sx={{ mb: 4, display: "flex", gap: 2 }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FlagOutlinedIcon />}
                            sx={{
                              borderColor: "#ffffff",
                              color: isFlagged ? "#EA580C" : "#2196f3",
                              textTransform: "none",
                              fontWeight: 500,
                              borderRadius: "8px",
                              fontSize: "14px", // This should work for the button text
                              backgroundColor: isFlagged
                                ? "#FFF7ED"
                                : "transparent",
                              "&:hover": {
                                borderColor: isFlagged ? "#ffffff" : "#e5e7eb",
                                backgroundColor: isFlagged
                                  ? "#38C172"
                                  : "#e3f2fd",
                                color: isFlagged ? "#ffffff" : "#2196f3",
                              },
                            }}
                          // onClick={() => {
                          //   dispatch(
                          //     addFalg({
                          //       student_test_id: ListStudentExam?.id,
                          //       question_id: questions[currentQuestion - 1]?.id,
                          //     })
                          //   );
                          //   if (isFlagged) {
                          //     setShowUnflagAlert(true);
                          //   } else {
                          //     setOpenflag(true);
                          //   }
                          // }}
                          >
                            {isFlagged ? "Flagged for Review" : "Flag for Review"}
                          </Button>
                          {questions[0]?.question_type ===
                            "Writing" ? null : (
                            <div style={{ display: "flex" }}>
                              <Button
                                variant={
                                  isOptionSelected ? "contained" : "outlined"
                                }
                                sx={{
                                  color: isOptionSelected ? "#fff" : "#767e89",
                                  backgroundColor: isOptionSelected
                                    ? "#111"
                                    : "transparent",
                                  borderColor: "#e5e7eb",
                                  fontSize: "14px",
                                  textTransform: "capitalize",
                                  "&:hover": {
                                    backgroundColor: isOptionSelected
                                      ? "#222"
                                      : "#f5f5f5",
                                    color: isOptionSelected ? "#fff" : "#767e89",
                                  },
                                }}
                                disabled={!isOptionSelected}
                                onClick={clearSelection}
                              >
                                Clear Answer
                              </Button>
                            </div>
                          )}
                        </Box>
                      </Typography>
                    </div>
                  </Grid>
                </div>
              </Card>
              <Typography
                variant="body2"
                sx={{
                  m: 3,
                  fontSize: "14px",
                  color: "#6b7280",
                  textAlign: "center",
                  paddingBottom: "80px",
                }}
              >
                Keyboard shortcuts: → / ← to navigate,
                <span className="key-style"> C</span> to clear answer
                <span className="key-style"> F</span> to flag answer
              </Typography>
            </>
          ) : questions[0]?.question_type === "Writing" ? (
            <Card
              sx={{
                margin: "2% 0%",
                borderRadius: "0px",
                boxShadow: "0px 37px 37px rgba(0, 157, 255, 0.1)",
                overflow: "visible",
                // Removed fixed minHeight; let it grow
                height: "auto",
              }}
            >
              <div
                className="flex_row_col"
                style={{
                  display: "flex",
                  height: "auto",
                  minHeight: "calc(100vh - 300px)",
                }}
              >
                {" "}
                {/* Adjusted for visibility */}
                <Grid
                  item
                  xs={12}
                  md={6}
                  sm={12}
                  sx={{
                    width: "50%",
                    borderRight: "1px solid #e4e4e4",
                    height: "100%",
                    overflowY: "auto", // Enable scroll for left panel
                  }}
                >
                  <Box
                    sx={{ p: 2, backgroundColor: "#fff", minHeight: "100%" }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        backgroundColor: "#009dff",
                        color: "#fff",
                        p: "10px",
                        borderRadius: 1,
                        mb: 2,
                        fontWeight: 500,
                      }}
                    >
                      Creative Writing
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, fontSize: "17px", mb: 2 }}
                    >
                      Use this idea as the basis for a piece of writing. You may
                      write in any style.
                    </Typography>

                    <Paper
                      variant="outlined"
                      sx={{
                        height: "auto",
                        mb: 2,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#f9fafb",
                        p: 2
                      }}
                    >
                      {imageFile && (
                        <div className="w-full rounded-xl border border-yellow-300 bg-yellow-50 p-5 shadow-sm">
                          <h2
                            className="font-bold flex items-center gap-2"
                            style={{ color: "#c96726b9", fontSize: "1.5rem" }}
                          >
                            <span>
                              <LucideImage />
                            </span>{" "}
                            Image Size Control
                          </h2>

                          <div
                            className="mt-2"
                            style={{ color: "#c96726b9", fontSize: "1.2rem" }}
                          >
                            Adjust the image size to ensure optimal display.
                          </div>

                          {/* Presets */}
                          <div className="mt-3 flex flex-wrap gap-2 items-center">
                            <span className="text-lg text-black-900">Presets:</span>
                            {presets.map((preset) => (
                              <button
                                key={preset.value}
                                onClick={() => setImageWidth(preset.value)}
                                className={`px-3 py-1.5 rounded-lg border transition text-sm
              ${imageWidth == preset.value
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                  }`}
                              >
                                <span className={`text-lg font-bold`}>
                                  {preset.label}
                                </span>
                              </button>
                            ))}
                          </div>

                          {/* Slider */}
                          <div className="mt-2 flex items-center gap-3 w-full">
                            <span className="text-gray-500">
                              <LucideZoomOut size={18} />
                            </span>

                            <Slider
                              value={imageWidth}
                              min={20}
                              max={100}
                              step={1}
                              onChange={(_, value) => setImageWidth(value as number)}
                            />

                            <span className="text-gray-500">
                              <LucideZoomIn size={18} />
                            </span>

                            <span className="font-bold text-lg">{imageWidth + 40}%</span>
                          </div>

                          {/* Save button */}
                          <div className="mt-2">
                            <button
                              className="bg-orange-500 text-white text-lg px-5 py-2 rounded-lg shadow hover:bg-orange-600 transition"
                              onClick={async () => {
                                setSavingType('prompt');
                                dispatch(
                                  EditQuestions({
                                    id: examtamplete?.id,
                                    imgWidth: imageWidth,
                                  })
                                );
                              }}
                            >
                              {isEditLoading
                                ? 'Saving...'
                                : 'Save Image Size to Database'}
                            </button>
                          </div>
                        </div>
                      )}
                      <br />
                      {questions[0]?.question_url && (
                        <Snackbar
                          open={alert.open}
                          autoHideDuration={6000}
                          onClose={() => setAlert({ ...alert, open: false })}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          sx={{
                            bottom: { xs: '90px', sm: '80px' },
                          }}
                        >
                          <Alert
                            onClose={() => setAlert({ ...alert, open: false })}
                            severity={alert.severity}
                            sx={{ width: '100%', backgroundColor: "#fff", fontWeight: "600" }}
                          >
                            {alert.message}
                          </Alert>
                        </Snackbar>
                      )}
                      <br />
                      <img
                        src={imageFile}
                        alt="Writing Prompt"
                        style={{
                          width: `${imageWidth || 90}%`,
                          borderRadius: "8px",
                        }}
                      />
                    </Paper>

                    <Paper
                      elevation={0}
                      sx={{
                        backgroundColor: "#E9F4FF",
                        borderRadius: "8px",
                        borderLeft: "4px solid #009dff",
                        p: 2,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#009dff",
                          width: "24px",
                          height: "24px",
                          borderRadius: "5px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        <ArticleOutlinedIcon fontSize="small" />
                      </Box>
                      <Typography fontSize="14px" sx={{ lineHeight: 1.5 }}>
                        Look at the image above and write a creative piece
                        inspired by what you see. You may choose any writing
                        style - narrative, descriptive, persuasive, or
                        analytical.
                      </Typography>
                    </Paper>

                    <Grid container spacing={2} mb={2} mt={0}>
                      {[
                        { label: "Words", value: wordCount },
                        { label: "Characters", value: characterCount },
                        { label: "Sentences", value: sentenceCount },
                        { label: "Paragraphs", value: paragraphCount },
                      ].map((item, index) => (
                        <Grid item xs={6} key={index}>
                          <Paper
                            elevation={0}
                            sx={{
                              backgroundColor: "#f9f9f9",
                              p: 2,
                              textAlign: "center",
                              borderRadius: 2,
                            }}
                          >
                            <Typography variant="h6" fontWeight={700}>
                              {item.value}
                            </Typography>
                            <Typography fontSize="13px">
                              {item.label}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>

                    {wordCount < 200 && (
                      <Box
                        sx={{
                          backgroundColor: "#FFEEDB",
                          color: "#C85A00",
                          p: 1,
                          borderRadius: 10,
                          textAlign: "center",
                          fontSize: "14px",
                          mb: 1,
                        }}
                      >
                        Write between 200–300 words
                      </Box>
                    )}

                    {remainingWords > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <ErrorOutlineIcon sx={{ color: "#f44336" }} />
                        <Typography
                          sx={{
                            color: "#f44336",
                            p: 1,
                            borderRadius: 10,
                            textAlign: "center",
                            fontSize: "14px",
                          }}
                        >
                          Need {remainingWords} more words
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sm={12}
                  sx={{
                    width: "50%",
                    borderRadius: "0 12px 12px 0",
                    overflow: "hidden",
                    paddingBottom: "10px",
                    height: "100%",
                    overflowY: "auto", // Enable scroll for right panel (editor)
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        px: 2,
                        pb: 2,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: "17px",
                          mb: 2,
                          mt: 2,
                          marginLeft: "5px",
                        }}
                      >
                        Your Response:
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        {renderQuestionOptions(
                          currentQuestion,
                          questions[0]?.question_type,
                          questions[0]?.options || [],
                          questions[0]?.optionsurl || []
                        )}
                      </Box>
                    </Box>
                  </div>
                </Grid>
              </div>
            </Card>
          ) : (
            <Grid
              item
              xs={12}
              sx={{ margin: "0px 4% 10px 4%", height: "auto" }}
            >
              {" "}
              {/* Adjusted height */}
              <Card
                sx={{
                  m: 2,
                  borderRadius: "20px",
                  boxShadow: "0px 37px 37px rgba(0, 157, 255, 0.1)",
                  height: "auto", // Let it grow
                  minHeight: "calc(100vh - 300px)", // Minimum to ensure visibility
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "white",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "capitalize",
                    padding: "25px",
                    backgroundColor: "#009dff",
                    mb: 1,
                  }}
                >
                  {questions[0]?.subtype_name}
                </Typography>
                <div
                  style={{ margin: "2% 3%", height: "auto", overflowY: "auto" }}
                >
                  {" "}
                  {/* Enable scroll */}
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      fontSize: "19px",
                      wordBreak: "break-word",
                    }}
                  >
                    {questions[0]?.prompt}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      fontSize: "19px",
                      wordBreak: "break-word",
                    }}
                  >
                    {questions[0]?.instruction}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      fontSize: "20px",
                      wordBreak: "break-word",
                    }}
                  >
                    {questions[0]?.question}
                  </Typography>
                  {questions[0]?.question_url && (
                    <Box
                      sx={{
                        width: "100%",
                        bgcolor: "#f8f9fa",
                        borderRadius: "10px",
                        p: 2,
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      {imageFile && (
                        <div className="w-full rounded-xl border border-yellow-300 bg-yellow-50 p-5 shadow-sm">
                          <h2
                            className="font-bold flex items-center gap-2"
                            style={{ color: "#c96726b9", fontSize: "1.5rem" }}
                          >
                            <span>
                              <LucideImage />
                            </span>{" "}
                            Image Size Control
                          </h2>

                          <div
                            className="mt-2"
                            style={{ color: "#c96726b9", fontSize: "1.2rem" }}
                          >
                            Adjust the image size to ensure optimal display.
                          </div>

                          {/* Presets */}
                          <div className="mt-3 flex flex-wrap gap-2 items-center">
                            <span className="text-lg text-black-900">Presets:</span>
                            {presets.map((preset) => (
                              <button
                                key={preset.value}
                                onClick={() => setImageWidth(preset.value)}
                                className={`px-3 py-1.5 rounded-lg border transition text-sm
              ${imageWidth == preset.value
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                  }`}
                              >
                                <span className={`text-lg font-bold`}>
                                  {preset.label}
                                </span>
                              </button>
                            ))}
                          </div>

                          {/* Slider */}
                          <div className="mt-2 flex items-center gap-3 w-full">
                            <span className="text-gray-500">
                              <LucideZoomOut size={18} />
                            </span>

                            <Slider
                              value={imageWidth}
                              min={20}
                              max={100}
                              step={1}
                              onChange={(_, value) => setImageWidth(value as number)}
                            />

                            <span className="text-gray-500">
                              <LucideZoomIn size={18} />
                            </span>

                            <span className="font-bold text-lg">{imageWidth + 40}%</span>
                          </div>

                          {/* Save button */}
                          <div className="mt-2">
                            <button
                              className="bg-orange-500 text-white text-lg px-5 py-2 rounded-lg shadow hover:bg-orange-600 transition"
                              onClick={async () => {
                                setSavingType('prompt');
                                dispatch(
                                  EditQuestions({
                                    id: examtamplete?.id,
                                    imgWidth: imageWidth,
                                  })
                                );
                              }}
                            >
                              {isEditLoading
                                ? 'Saving...'
                                : 'Save Image Size to Database'}
                            </button>
                          </div>
                        </div>
                      )}
                      <br />
                      <img
                        src={imageFile}
                        alt="Question visual"
                        className="resizable-image"
                        style={{
                          width: `${imageWidth}%`,
                          // height: "auto",
                        }}
                      />
                    </Box>
                  )}
                  <Grid container spacing={2}>
                    {renderQuestionOptions(
                      currentQuestion,
                      questions[0]?.question_type,
                      questions[0]?.options || [],
                      questions[0]?.optionsurl || []
                    )}
                  </Grid>
                  <Box
                    sx={{
                      mt: 4,
                      mb: 2,
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<FlagOutlinedIcon />}
                      sx={{
                        borderColor: isFlagged ? "#EA580C" : "#e5e7eb",
                        color: isFlagged ? "#EA580C" : "#2196f3",
                        textTransform: "none",
                        fontWeight: 500,
                        borderRadius: "8px",
                        fontSize: "14px",
                        backgroundColor: isFlagged ? "#FFF7ED" : "transparent",
                        minWidth: "auto",
                        px: 2,
                        "&:hover": {
                          borderColor: isFlagged ? "#EA580C" : "#2196f3",
                          backgroundColor: isFlagged ? "#FFF7ED" : "#e3f2fd",
                          color: isFlagged ? "#EA580C" : "#2196f3",
                        },
                      }}
                      onClick={toggleFlag}
                    >
                      {isFlagged ? "Flagged for Review" : "Flag for Review"}
                    </Button>
                    <Button
                      variant={isOptionSelected ? "contained" : "outlined"}
                      sx={{
                        color: isOptionSelected ? "#FFF" : "#767e89",
                        backgroundColor: "transparent",
                        borderColor: "#e5e7eb",
                        fontSize: "14px",
                        textTransform: "capitalize",
                        minWidth: "auto",
                        px: 2,
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          color: isOptionSelected ? "#36454F" : "#767e89",
                        },
                      }}
                      disabled={!isOptionSelected}
                      onClick={clearSelection}
                    >
                      Clear Answer
                    </Button>
                  </Box>
                </div>
              </Card>
            </Grid>
          )}
        </Box>

        {/* Sticky navigation bar at bottom */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#f5f9ff",
            borderTop: "1px solid #e0e0e0",
            zIndex: 1000,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0, // Prevent shrinking
          }}
          className="navigation-buttons"
        >
          <Button
            className="buttonnameprevious"
            sx={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "none",
              fontWeight: 500,
              fontSize: "16px",
              padding: "8px 24px",
              minWidth: "120px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#f5f9ff",
                borderColor: "#009dff",
              },
            }}
            onClick={() => setCurrentQuestion((prev) => Math.max(prev - 1, 1))}
            disabled={currentQuestion === 1}
          >
            <ArrowBackIcon sx={{ marginRight: "10px", color: "black" }} />{" "}
            Previous
          </Button>

          <Button
            className="buttonnamenextsreview"
            variant="contained"
            color="primary"
            onClick={handleBack}
          >
            {props.onBack ? "Back" : "Back to question page"}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={showFlagAlert}
        autoHideDuration={2000}
        onClose={() => setShowFlagAlert(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          "& .MuiSnackbar-root": {
            position: "fixed",
            bottom: "80px", // Adjusted to account for sticky nav (~60px)
            right: "20px",
          },
        }}
      >
        <Alert
          severity="success"
          onClose={() => setShowFlagAlert(false)}
          sx={{
            width: "100%",
            backgroundColor: "#4caf50",
            color: "white",
            "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          {flagAlertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OnlineExamTamplete;
